import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Badge,
  CardMedia,
  Slide,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar
} from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { statistiquesService } from '../../services/statistiquesService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Stats() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'offres' ? 1 : 0
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entreprisesData, setEntreprisesData] = useState(null);
  const [offresData, setOffresData] = useState(null);

  const [slideDirection, setSlideDirection] = useState('right');
  const [slideIn, setSlideIn] = useState(true);
  const [currentWishlistIndex, setCurrentWishlistIndex] = useState(0);

  const processStats = useCallback((stats, type) => {
    if (type === 'company' && stats.entreprises?.repartition_secteurs) {
      setEntreprisesData({
        secteursChartData: {
          labels: stats.entreprises.repartition_secteurs.map(item => item.secteur),
          datasets: [{
            label: 'Nombre d\'entreprises par secteur',
            data: stats.entreprises.repartition_secteurs.map(item => item.nombre),
            backgroundColor: theme.palette.stats.sectors,
          }]
        }
      });
    }

    if (type === 'offers' && stats.offres) {
      setOffresData({
        competencesChartData: {
          labels: stats.offres.top_competences.map(item => item.technologie),
          datasets: [{
            label: 'Nombre d\'offres',
            data: stats.offres.top_competences.map(item => item.nombre),
            backgroundColor: theme.palette.stats.competences.bar,
            borderColor: theme.palette.stats.competences.bar,
            borderWidth: 1
          }]
        },
        dureeChartData: {
          labels: stats.offres.repartition_duree.map(item => item.duree),
          datasets: [{
            label: 'Nombre d\'offres par durée',
            data: stats.offres.repartition_duree.map(item => item.nombre),
            backgroundColor: theme.palette.stats.durations
          }]
        },
        topWishlist: stats.offres.top_wishlist
      });
    }
  }, [theme]);

  const fetchStats = useCallback(async () => {
    const apiValue = activeTab === 1 ? 'offers' : 'company';
    
    try {
      setLoading(true);
      const { stats } = await statistiquesService.getStats(apiValue);
      processStats(stats, apiValue);
    } catch (err) {
      console.error('❌ Erreur dans fetchStats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, processStats]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const newTab = tab === 'offres' ? 1 : 0;
    if (activeTab !== newTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchParams({ tab: newValue === 1 ? 'offres' : 'entreprises' });
  };

  const handleNext = () => {
    setSlideDirection('left');
    setSlideIn(false);
    setTimeout(() => {
      setCurrentWishlistIndex((prev) => 
        prev === (offresData?.topWishlist?.length - 1 || 0) ? 0 : prev + 1
      );
      setSlideDirection('right');
      setSlideIn(true);
    }, 300);
  };

  const handlePrev = () => {
    setSlideDirection('right');
    setSlideIn(false);
    setTimeout(() => {
      setCurrentWishlistIndex((prev) => 
        prev === 0 ? (offresData?.topWishlist?.length - 1 || 0) : prev - 1
      );
      setSlideDirection('left');
      setSlideIn(true);
    }, 300);
  };

  const handleOffreClick = (offreId) => {
    navigate(`/offres/${offreId}`, {
      state: {
        from: '/stats',
        tab: 'offres'
      }
    });
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
      </Container>
    );
  }

  const competencesChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: {
            size: 14
          },
          color: theme.palette.text.primary
        },
        title: {
          display: true,
          text: "Nombre d'offres",
          font: {
            size: 14
          },
          color: theme.palette.text.primary
        }
      },
      y: {
        ticks: {
          font: {
            size: 14
          },
          color: theme.palette.text.primary
        }
      }
    }
  };

  const dureeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 14
          },
          color: theme.palette.text.primary
        }
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography 
        variant="h3" 
        component="h1" 
        align="center"
        gutterBottom
        sx={{
          opacity: 0,
          animation: 'fadeIn 0.8s ease-out forwards',
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}
      >
        Statistiques
      </Typography>

      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        mb: 3,
        display: 'flex',
        justifyContent: 'center',
        opacity: 0,
        animation: 'fadeIn 0.8s ease-out 0.3s forwards',
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 }
        }
      }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          centered
        >
          <Tab label="Entreprises" />
          <Tab label="Offres" />
        </Tabs>
      </Box>

      {loading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {activeTab === 0 && entreprisesData && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Répartition par secteur d'activité
                    </Typography>
                    <Box sx={{ height: 400 }}>
                      <Pie 
                        data={entreprisesData.secteursChartData} 
                        options={dureeChartOptions} 
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && offresData && (
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top 10 des compétences recherchées
                    </Typography>
                    <Box sx={{ height: 400 }}>
                      <Bar 
                        data={offresData.competencesChartData} 
                        options={competencesChartOptions}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Répartition par durée de stage
                    </Typography>
                    <Box sx={{ height: 400 }}>
                      <Pie 
                        data={offresData.dureeChartData} 
                        options={dureeChartOptions}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ 
                  background: theme.palette.stats.competences.background,
                  boxShadow: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}>
                  <CardContent>
                    <Typography 
                      variant="h6" 
                      gutterBottom 
                      sx={{ 
                        textAlign: 'center', 
                        color: 'text.primary', 
                        fontWeight: 'bold', 
                        mb: 3 
                      }}
                    >
                      Top 5 des offres les plus mises en wishlist
                    </Typography>
                    <Box sx={{ position: 'relative', py: 2, overflow: 'hidden', height: 400 }}>
                      {offresData.topWishlist && offresData.topWishlist.length > 0 && (
                        <>
                          <IconButton
                            onClick={handlePrev}
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 2,
                              bgcolor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.9)'
                              }
                            }}
                          >
                            <ArrowBack />
                          </IconButton>

                          <Slide 
                            direction={slideDirection} 
                            in={slideIn} 
                            mountOnEnter 
                            unmountOnExit 
                            timeout={300}
                          >
                            <Card 
                              sx={{ 
                                maxWidth: 600, 
                                mx: 'auto',
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: 6
                                },
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                position: 'relative'
                              }}
                              onClick={() => handleOffreClick(offresData.topWishlist[currentWishlistIndex].id)}
                            >
                              <Badge
                                badgeContent={currentWishlistIndex + 1}
                                color="primary"
                                sx={{
                                  position: 'absolute',
                                  top: 24,
                                  right: 24,
                                  zIndex: 1,
                                  '& .MuiBadge-badge': {
                                    fontSize: '1.2rem',
                                    height: '2.5rem',
                                    width: '2.5rem',
                                    borderRadius: '50%',
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    fontWeight: 'bold'
                                  }
                                }}
                              />
                              {offresData.topWishlist[currentWishlistIndex] && (
                                <>
                                  <CardMedia
                                    component="img"
                                    height="160"
                                    image={offresData.topWishlist[currentWishlistIndex].logo || '/default-logo.png'}
                                    alt={offresData.topWishlist[currentWishlistIndex].titre || 'Titre non disponible'}
                                    sx={{ 
                                      objectFit: 'contain', 
                                      p: 3, 
                                      backgroundColor: 'background.default' 
                                    }}
                                  />
                                  <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" gutterBottom>
                                      {offresData.topWishlist[currentWishlistIndex].titre || 'Titre non disponible'}
                                    </Typography>
                                    <Typography variant="subtitle1">
                                      {offresData.topWishlist[currentWishlistIndex].entreprise || 'Entreprise non disponible'}
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'primary.main', 
                                        fontWeight: 'bold' 
                                      }}
                                    >
                                      Ajouté {offresData.topWishlist[currentWishlistIndex].nombre_wishlist} fois en wishlist
                                    </Typography>
                                  </CardContent>
                                </>
                              )}
                            </Card>
                          </Slide>

                          <IconButton
                            onClick={handleNext}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 2,
                              bgcolor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.9)'
                              }
                            }}
                          >
                            <ArrowForward />
                          </IconButton>
                        </>
                      )}
                      {(!offresData.topWishlist || offresData.topWishlist.length === 0) && (
                        <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                          Aucune offre en wishlist pour le moment
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Container>
  );
}

export default Stats;